"use client";

import React from "react";
import { NavSearch, type NavSearchProps } from "./nav-search.client";
import { useSearchSuggestions } from "./use-search-suggestions";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * NavSearch with its suggestions fetched: owns `useSearchSuggestions`, so the
 * state sits in the leaf that reads it and the page passes one string instead
 * of becoming a client component to hold it. Same shape as NavCart owning its
 * own state. Use `NavSearch` directly only when the host already fetches.
 */
export interface NavSearchLiveProps
  extends Omit<
    NavSearchProps,
    "results" | "onQueryChange" | "loading" | "errorMessage" | "errorCode"
  > {
  /**
   * Where the two suggesters live, relative — `/searchResults/api`. Required:
   * only the host knows the path.
   * ⚠ NO app base path. They are site-level, not under `/uf`, which is also
   * why the search form's own action carries none.
   */
  suggestBase: string;
  /**
   * Shown when the suggesters fail. Required: the copy is the host's
   * (HIM-4761, OQ-12), and a failure with no message cannot be told apart
   * from "not answered yet".
   */
  unavailableMessage: string;
}

function NavSearchLiveImpl({
  suggestBase,
  unavailableMessage,
  ...navSearch
}: NavSearchLiveProps): JSX.Element {
  const { results, loading, error, errorCode, onQueryChange } = useSearchSuggestions(suggestBase);
  return (
    <NavSearch
      {...navSearch}
      errorCode={error ? errorCode : undefined}
      errorMessage={error ? unavailableMessage : undefined}
      loading={loading}
      onQueryChange={onQueryChange}
      results={results}
    />
  );
}
export const NavSearchLive = withSafeRender(NavSearchLiveImpl, "NavSearchLive");
